/**
 * Audio Context Management Utilities
 * Handles creation, suspension, and resumption of audio contexts
 */

import { logger } from '@/lib/logger'

export class AudioContextManager {
  private static instance: AudioContextManager
  private audioContext: AudioContext | null = null
  private contextPromise: Promise<AudioContext> | null = null

  private constructor() {}

  static getInstance(): AudioContextManager {
    if (!AudioContextManager.instance) {
      AudioContextManager.instance = new AudioContextManager()
    }
    return AudioContextManager.instance
  }

  async getAudioContext(): Promise<AudioContext> {
    // Check if we have an existing context and if it's still valid
    if (this.audioContext) {
      // Check if the context is closed or in an invalid state
      if (this.audioContext.state === 'closed') {
        logger.log('AudioContext was closed, creating new one')
        this.audioContext = null
        this.contextPromise = null
      } else if (this.audioContext.state === 'running' || this.audioContext.state === 'suspended') {
        logger.log('Returning existing AudioContext:', this.audioContext.state)
        return this.audioContext
      }
    }

    if (this.contextPromise) {
      logger.log('AudioContext creation already in progress, waiting...')
      return this.contextPromise
    }

    logger.log('Creating new AudioContext...')
    this.contextPromise = this.createAudioContext()

    try {
      this.audioContext = await this.contextPromise
      this.contextPromise = null
      logger.log('AudioContext created successfully:', this.audioContext.state)
      return this.audioContext
    } catch (error) {
      this.contextPromise = null
      logger.error('Failed to create AudioContext:', error)
      throw error
    }
  }

  private async createAudioContext(): Promise<AudioContext> {
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined') {
        throw new Error('AudioContext is only available in browser environment. Please run this application in a web browser.')
      }

      // Check if document is ready
      if (typeof document === 'undefined') {
        throw new Error('Document not available. Audio context requires a DOM environment.')
      }

      // Use native browser AudioContext
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext

      if (!AudioContextClass) {
        const browserInfo = this.getBrowserInfo()
        throw new Error(`AudioContext not supported in ${browserInfo}. Please update your browser or use a modern browser like Chrome, Firefox, or Safari.`)
      }

      // Create audio context with specific options for better compatibility
      const context = new AudioContextClass({
        latencyHint: 'interactive',
        sampleRate: undefined // Let browser choose optimal sample rate
      })

      // Handle suspended state - many browsers require user interaction
      if (context.state === 'suspended') {
        logger.log('AudioContext created but suspended - user interaction will be required to start audio')

        // Note: Event listeners for user interaction will be handled by the main app
        // We don't attempt immediate resume here as it can cause timeouts in some browsers
        // The AudioContext will be resumed when the user interacts with the application
      }

      logger.log(`AudioContext created successfully: ${context.sampleRate}Hz, state: ${context.state}`)
      return context
    } catch (error) {
      logger.error('Failed to create AudioContext:', error)

      // Provide more specific error messages based on error type
      if (error instanceof Error) {
        if (error.message.includes('browser environment')) {
          throw error // Re-throw as-is
        } else if (error.message.includes('not supported')) {
          throw error // Re-throw as-is
        } else if (error.name === 'NotSupportedError') {
          throw new Error(`AudioContext not supported: ${error.message}. Please update your browser to a newer version.`)
        } else if (error.name === 'SecurityError') {
          throw new Error(`Audio blocked by browser security: ${error.message}. Try refreshing the page or disabling browser extensions that might block audio.`)
        } else if (error.name === 'NotAllowedError') {
          throw new Error(`Audio permission denied: ${error.message}. The browser blocked audio access. Try interacting with the page first (click anywhere) and then refresh.`)
        } else {
          throw new Error(`Audio initialization failed: ${error.message}. This may be due to browser security restrictions, hardware limitations, or conflicting browser extensions.`)
        }
      }

      throw new Error('Unknown audio initialization error occurred')
    }
  }

  private getBrowserInfo(): string {
    if (typeof navigator === 'undefined') return 'unknown browser'

    const userAgent = navigator.userAgent
    if (userAgent.includes('Chrome')) return 'Chrome'
    if (userAgent.includes('Firefox')) return 'Firefox'
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari'
    if (userAgent.includes('Edge')) return 'Edge'
    if (userAgent.includes('Opera')) return 'Opera'

    return 'your current browser'
  }

  /**
   * Comprehensive browser compatibility check for audio features
   */
  public static checkAudioCompatibility(): {
    isCompatible: boolean
    issues: string[]
    recommendations: string[]
  } {
    const issues: string[] = []
    const recommendations: string[] = []

    // Check if running in browser
    if (typeof window === 'undefined') {
      issues.push('Not running in a browser environment')
      recommendations.push('Run this application in a web browser')
      return { isCompatible: false, issues, recommendations }
    }

    // Check AudioContext support
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext
    if (!AudioContextClass) {
      issues.push('AudioContext not supported')
      recommendations.push('Update to a modern browser (Chrome 14+, Firefox 25+, Safari 6+)')
    }

    // Check AudioWorklet support (optional but recommended)
    if (!window.AudioWorklet || typeof AudioWorkletNode === 'undefined') {
      issues.push('AudioWorklet not fully supported (optional)')
      recommendations.push('Use Chrome 66+ or Firefox 76+ for best performance')
    }

    // Check Web Audio API features
    try {
      const testContext = new AudioContextClass()
      if (!testContext.createBuffer) {
        issues.push('AudioBuffer creation not supported')
      }
      if (!testContext.createBufferSource) {
        issues.push('AudioBufferSourceNode not supported')
      }
      if (!testContext.destination) {
        issues.push('Audio destination not available')
      }
      testContext.close()
    } catch (error) {
      issues.push('AudioContext creation failed')
      recommendations.push('Check browser security settings and hardware permissions')
    }

    // Check media capabilities
    if (!navigator.mediaDevices) {
      issues.push('Media devices not available (optional)')
    }

    const isCompatible = issues.length === 0 || issues.every(issue => issue.includes('(optional)'))

    if (!isCompatible && issues.length > 0) {
      recommendations.push('Try refreshing the page or restarting your browser')
      recommendations.push('Check if any browser extensions are blocking audio')
    }

    return { isCompatible, issues, recommendations }
  }

  async suspend(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'running') {
      await this.audioContext.suspend()
    }
  }

  async resume(): Promise<boolean> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume()
        logger.log('AudioContext resumed successfully')
        return true
      } catch (error) {
        logger.error('Failed to resume AudioContext:', error)
        return false
      }
    }
    return true // Already running or not suspended
  }

  async autoResume(): Promise<boolean> {
    // Force resume audio context - try multiple times with delays
    let attempts = 0
    const maxAttempts = 5

    while (attempts < maxAttempts) {
      try {
        const success = await this.resume()
        if (success) {
          logger.log('✅ AudioContext auto-resumed successfully')
          return true
        }
      } catch (error) {
        logger.warn(`AudioContext resume attempt ${attempts + 1} failed:`, error)
      }

      attempts++
      if (attempts < maxAttempts) {
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    }

    logger.warn('❌ AudioContext could not be auto-resumed after multiple attempts')
    return false
  }

  getContextState(): AudioContextState | null {
    return this.audioContext?.state || null
  }

  dispose(): void {
    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }
    this.contextPromise = null
  }

  reset(): void {
    logger.log('Resetting AudioContext manager')
    if (this.audioContext) {
      try {
        this.audioContext.close()
      } catch (error) {
        logger.warn('Error closing AudioContext during reset:', error)
      }
    }
    this.audioContext = null
    this.contextPromise = null
  }
}

export const audioContextManager = AudioContextManager.getInstance()

/**
 * Utility function to get audio context with automatic error handling
 */
export async function getAudioContext(): Promise<AudioContext> {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    throw new Error('AudioContext is only available in browser environment')
  }

  return audioContextManager.getAudioContext()
}

/**
 * Utility function to ensure audio context is running
 */
export async function ensureAudioContextRunning(): Promise<AudioContext> {
  const context = await getAudioContext()

  if (context.state === 'suspended') {
    await context.resume()
  }

  return context
}

/**
 * Utility function to reset the audio context manager
 * Useful for cleaning up state on page reloads or when starting fresh
 */
export function resetAudioContext(): void {
  audioContextManager.reset()
}

/**
 * Utility function to check if audio context needs reset
 */
export function shouldResetAudioContext(): boolean {
  const state = audioContextManager.getContextState()
  return state === 'closed' || state === null
}

/**
 * Utility function to get the appropriate AudioWorklet processor name
 * Returns the best available processor or null if none are available
 */
export async function getAvailableAudioWorkletProcessor(audioContext: AudioContext): Promise<string | null> {
  if (!audioContext.audioWorklet || typeof AudioWorkletNode === 'undefined') {
    return null
  }

  // Try main processor first
  try {
    await audioContext.audioWorklet.addModule('/audio-worklets/sws-neural-processor.js')
    return 'sws-neural-processor'
  } catch (error) {
    logger.warn('Main AudioWorklet processor not available:', error)

    // Try fallback processor
    try {
      await audioContext.audioWorklet.addModule('/audio-worklets/sws-fallback-processor.js')
      return 'sws-fallback-processor'
    } catch (fallbackError) {
      logger.warn('Fallback AudioWorklet processor also not available:', fallbackError)
      return null
    }
  }
}

/**
 * Utility function to create an AudioWorkletNode with fallback support
 */
export async function createAudioWorkletNode(audioContext: AudioContext, options?: AudioWorkletNodeOptions): Promise<AudioWorkletNode | null> {
  const processorName = await getAvailableAudioWorkletProcessor(audioContext)

  if (!processorName) {
    logger.warn('No AudioWorklet processors available')
    return null
  }

  try {
    return new AudioWorkletNode(audioContext, processorName, options)
  } catch (error) {
    logger.error('Failed to create AudioWorkletNode:', error)
    return null
  }
}